import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  LoginCredentials,
  LoginResponse,
  SSOApiResponse,
  User,
  loginRequiresMandatoryPasswordChange,
} from '@rfacil/shared/types';
import { apiSSO } from './api-client';
import { jwtDecode } from 'jwt-decode';
import { useUserContextStore } from './user-context-store';
import { usePermissionsStore } from './permissions-store';
import axios from 'axios';

/**
 * @interface AuthState
 * @description Define la forma del estado de autenticación gestionado por Zustand.
 */
export interface AuthState {
  /**
   * @property {User | null} user - El objeto de usuario autenticado, o nulo si no está autenticado.
   */
  user: User | null;
  /**
   * @property {string | null} token - El token de autenticación, o nulo si no está autenticado.
   */
  token: string | null;
  /**
   * @property {number | null} idUser - El ID del usuario, o nulo si no está autenticado.
   */
  idUser: number | null;
  /**
   * @property {number | null} IdCorporativo - El ID del corporativo, o nulo si no está autenticado.
   */
  idCorporativo: number | null;
  /**
   * @property {number | null} IdEmpresa - El ID de la empresa, o nulo si no está autenticado.
   */
  idEmpresa: number | null;
  /**
   * @property {boolean} isAuthenticated - Una bandera que indica si el usuario está autenticado.
   */
  isAuthenticated: boolean;
  /**
   * @property {boolean} isLoading - Una bandera que indica si hay un proceso de autenticación en curso.
   */
  isLoading: boolean;
  /**
   * @property {string | null} error - Un mensaje de error si un proceso de autenticación falla, o nulo en caso contrario.
   */
  error: string | null;
  /**
   * @property {string | null} expiracion - La fecha de expiración del token en formato ISO 8601, o nulo si no está autenticado.
   */
  expiracion: string | null;
  /**
   * @property {boolean} pendingPasswordChange - Si es true, el usuario debe completar el cambio de contraseña (bloquea el uso de la app).
   */
  pendingPasswordChange: boolean;
  /**
   * @property {boolean} showVoluntaryPasswordChange - Abre la misma pantalla de cambio de contraseña desde el menú de usuario (opcional).
   */
  showVoluntaryPasswordChange: boolean;

  /**
   * @function login
   * @description Una función para iniciar sesión del usuario con las credenciales dadas.
   * @param {LoginCredentials} credentials - Las credenciales de inicio de sesión del usuario.
   * @returns {Promise<void>} - Una promesa que se resuelve cuando se completa el inicio de sesión.
   */
  login: (credentials: LoginCredentials) => Promise<void>;
  /**
   * @function logout
   * @description Una función para cerrar la sesión del usuario actual.
   */
  logout: () => void;
  /**
   * @function clearPendingPasswordChange
   * @description Marca como completado el cambio de contraseña obligatorio y cierra el flujo voluntario si aplica.
   */
  clearPendingPasswordChange: () => void;
  /**
   * @function openVoluntaryPasswordChange
   * @description Muestra la pantalla de cambio de contraseña sin ser el flujo obligatorio de primer ingreso.
   */
  openVoluntaryPasswordChange: () => void;
  /**
   * @function closeVoluntaryPasswordChange
   * @description Cierra la pantalla de cambio de contraseña abierta desde el menú.
   */
  closeVoluntaryPasswordChange: () => void;
  /**
   * @function hasPermission
   * @description Una función para comprobar si el usuario tiene permiso para acceder a un módulo específico.
   * @param {string} module - El nombre del módulo a comprobar.
   * @returns {boolean} - Verdadero si el usuario tiene permiso, falso en caso contrario.
   */
  hasPermission: (module: string) => boolean;
  /**
   * @function getPermissions
   * @description Una función para obtener la lista de permisos del usuario actual.
   * @returns {string[]} - Un array de cadenas de permisos.
   */
  getPermissions: () => string[];

}

/**
 * @constant useAuthStore
 * @description Un almacén de Zustand para gestionar el estado de autenticación.
 * Incluye el estado para el usuario, el token y el estado de autenticación, así como acciones para iniciar sesión, cerrar sesión y comprobar permisos.
 * El almacén se persiste en localStorage para mantener el estado de autenticación entre sesiones del navegador.
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<AuthState>>}
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      idUser: 0,
      expiracion: null,
      idCorporativo: 0,
      idEmpresa: 0,
      pendingPasswordChange: false,
      showVoluntaryPasswordChange: false,

      clearPendingPasswordChange: () =>
        set({ pendingPasswordChange: false, showVoluntaryPasswordChange: false }),

      openVoluntaryPasswordChange: () => set({ showVoluntaryPasswordChange: true }),

      closeVoluntaryPasswordChange: () => set({ showVoluntaryPasswordChange: false }),

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        try {
          const { data } = await apiSSO.post<SSOApiResponse<LoginResponse>>(
            '/Login/login',
            credentials
          );

          const loginData = data.Data;

          if (loginData.Token) {
            // Decodificar el token JWT para obtener todos los datos disponibles
            const tokenDecoded = jwtDecode<{
              IdUsuario: number;
              IdCorporativo: number;
              IdEmpresa: number;
              Correo: string;
              [key: string]: unknown;
            }>(loginData.Token);

            const idUser = tokenDecoded.IdUsuario;
            const idCorporativo = tokenDecoded.IdCorporativo;
            const idEmpresa = tokenDecoded.IdEmpresa;
            const correo = tokenDecoded.Correo;

            const user: User = {
              id: idUser,
              name: loginData.NombreCompleto,
              email: correo,
              role: loginData.Rol,
              permissions: loginData.ClavesMenu,
              nombreCompleto: loginData.NombreCompleto,
              expiracion: loginData.Expiracion,
              idCorporativo: idCorporativo,
              idEmpresa: idEmpresa,
            };

            // Establecer autenticación pero mantener isLoading en true
            // hasta que los permisos estén completamente cargados.
            // Esto evita que AuthRedirect navegue al Dashboard sin permisos,
            // lo cual causaba que PermissionRouteGuard bloqueara todas las rutas
            // después de un ciclo logout → re-login sin reload.
            set({
              user: user,
              token: loginData.Token,
              isAuthenticated: true,
              // isLoading se mantiene en true (del set inicial al comienzo de login)
              error: null,
              idUser: idUser,
              expiracion: loginData.Expiracion,
              idCorporativo: idCorporativo,
              idEmpresa: idEmpresa,
              pendingPasswordChange:
                loginRequiresMandatoryPasswordChange(loginData),
            });

            // Establecer IdCorporativo e IdEmpresa en el store de contexto si están disponibles
            if (tokenDecoded.IdCorporativo && tokenDecoded.IdCorporativo > 0) {
              useUserContextStore.getState().setCorporativo(tokenDecoded.IdCorporativo);
            } else {
              console.warn('⚠️ IdCorporativo no encontrado en el token JWT');
            }

            if (tokenDecoded.IdEmpresa && tokenDecoded.IdEmpresa > 0) {
              useUserContextStore.getState().setEmpresa(tokenDecoded.IdEmpresa);
            } else {
              console.warn('⚠️ IdEmpresa no encontrada en el token JWT');
            }

            // Cargar permisos de secciones y actividades ANTES de completar el login.
            // Esto es crítico: sin await, el usuario era redirigido al Dashboard
            // con sectionPermissions vacío, causando que useRolePermissions retorne
            // false para todos los permisos y PermissionRouteGuard bloquee todas las rutas.
            await usePermissionsStore.getState().fetchPermissions();

            // AHORA que los permisos están cargados, marcar isLoading como false.
            // AuthRedirect verifica (!isLoading && isAuthenticated) antes de navegar,
            // así que el usuario no será redirigido hasta tener permisos completos.
            set({ isLoading: false });

            window.dispatchEvent(
              new CustomEvent('userLoggedIn', {
                detail: {
                  User: user,
                  Token: loginData.Token,
                  Permissions: loginData.ClavesMenu,
                },
              })
            );
          }
        } catch (error) {
          console.error('❌ Error en login:', error);

          let errorMessage = 'Error al iniciar sesión';
          let errorType: 'user' | 'password' | 'other' = 'other';

          if (axios.isAxiosError(error)) {
            // Error de respuesta del servidor
            if (error.response) {
              const status = error.response.status;
              const responseData = error.response.data;

              // Obtener el mensaje de error del servidor
              const serverMessage =
                (typeof responseData === 'string' ? responseData : '') ||
                responseData?.message ||
                responseData?.Message ||
                '';

              // Normalizar el mensaje a minúsculas para comparación
              const normalizedMessage = serverMessage.toLowerCase();

              // Verificar si el error es relacionado con Base64/contraseña corrupta
              if (
                status === 500 &&
                (normalizedMessage.includes('base-64') ||
                  normalizedMessage.includes('base64') ||
                  normalizedMessage.includes('contraseña') && normalizedMessage.includes('corrupta'))
              ) {
                errorMessage =
                  'Error en el servidor: La contraseña almacenada para este usuario está corrupta o en un formato inválido. Contacta al administrador del sistema.';
                errorType = 'password';
              } else if (status === 401) {
                // Analizar el mensaje para determinar si es usuario o contraseña
                if (
                  normalizedMessage.includes('usuario no existe') ||
                  normalizedMessage.includes('usuario no encontrado') ||
                  normalizedMessage.includes('user not found') ||
                  normalizedMessage.includes('usuario inválido') ||
                  normalizedMessage.includes('invalid user') ||
                  normalizedMessage.includes('usuario incorrecto')
                ) {
                  errorMessage = 'El usuario no existe. Verifica que el usuario sea correcto.';
                  errorType = 'user';
                } else if (
                  normalizedMessage.includes('contraseña incorrecta') ||
                  normalizedMessage.includes('password incorrect') ||
                  normalizedMessage.includes('contraseña inválida') ||
                  normalizedMessage.includes('invalid password') ||
                  normalizedMessage.includes('wrong password')
                ) {
                  errorMessage = 'La contraseña es incorrecta. Verifica tu contraseña.';
                  errorType = 'password';
                } else {
                  // Si no podemos determinar el tipo específico, mostramos mensaje genérico
                  errorMessage =
                    'Credenciales inválidas. Verifica tu usuario y contraseña.';
                  errorType = 'other';
                }
              } else if (status === 404) {
                errorMessage = 'El usuario no existe. Verifica que el usuario sea correcto.';
                errorType = 'user';
              } else if (status === 500) {
                errorMessage =
                  'Error interno del servidor. Por favor, intenta nuevamente o contacta al soporte.';
                errorType = 'other';
              } else {
                errorMessage =
                  serverMessage || `Error del servidor (${status})`;
                errorType = 'other';
              }
            } else if (error.request) {
              // Error de error
              if (error.message.includes('timeout')) {
                errorMessage =
                  'El servidor no responde. Verifica que el backend esté ejecutándose en http://localhost:7260';
              } else if (error.message.includes('Network Error')) {
                errorMessage =
                  'Error de conexión. Verifica que el backend esté ejecutándose y accesible.';
              } else if (error.message.includes('ECONNREFUSED')) {
                errorMessage =
                  'No se puede conectar al backend. Verifica que esté ejecutándose en el puerto 7260.';
              } else {
                errorMessage = 'Error de conexión con el servidor.';
              }
              errorType = 'other';
            } else {
              errorMessage = error.message || 'Error al iniciar sesión';
              errorType = 'other';
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
            errorType = 'other';
          }

          // Log detallado para debugging
          if (axios.isAxiosError(error) && error.response) {
            console.error('📋 Detalles del error:', {
              status: error.response.status,
              data: error.response.data,
              headers: error.response.headers,
              errorType,
            });
          }

          set({
            isLoading: false,
            error: errorMessage,
          });

          // Crear un error personalizado con el tipo y mensaje
          const customError = new Error(errorMessage) as Error & { errorType?: 'user' | 'password' | 'other' };
          customError.errorType = errorType;
          throw customError;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          idUser: 0,
          error: null,
          expiracion: null,
          idCorporativo: 0,
          idEmpresa: 0,
          pendingPasswordChange: false,
          showVoluntaryPasswordChange: false,
        });

        // Limpiar también el contexto del usuario al hacer logout
        useUserContextStore.getState().clearAll();

        // Limpiar permisos de secciones y actividades al hacer logout
        usePermissionsStore.getState().clearPermissions();

        // Disparar evento de logout para que los remotes se enteren
        // Esto es crítico en Module Federation para que los remotes limpien su estado
        window.dispatchEvent(
          new CustomEvent('userLoggedOut', {
            detail: {
              timestamp: new Date().toISOString(),
            },
          })
        );

        // NO hacer window.location.reload() - el logout se maneja completamente
        // por React Router (ProtectedRoute redirige a /login cuando isAuthenticated = false).
        // El reload causaba una pantalla en blanco innecesaria y mala UX.
        // Los permisos se cargan correctamente en el siguiente login gracias al await
        // de fetchPermissions() en la función login().
      },

      hasPermission: (module: string) => {
        const state = get();

        if (!state.user || !state.user.permissions) {
          return false;
        }

        const hasAccess = state.user.permissions.some(
          (permission) => permission.toLowerCase() === module.toLowerCase()
        );
        return hasAccess;
      },
      getPermissions: () => {
        const state = get();
        return state.user?.permissions || [];
      },

    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        idUser: state.idUser,
        expiracion: state.expiracion,
        pendingPasswordChange: state.pendingPasswordChange,
      }),
    }
  )
);
